/**
 * @file blendGridSort.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-09-16
 * @description Sorting the blend grid by raw material cost per tonne.
 *
 *              The grid normally reads in the workbook's order, which is the
 *              right default and the wrong one for "what is the cheapest thing I
 *              could put in here". Sorting by £/T answers that — most usefully
 *              with every material shown.
 *
 *              Three clicks, not two: cheapest first, dearest first, then back
 *              to the workbook order. Other sortable tables always have SOME
 *              order to toggle between; this grid has a natural one worth
 *              getting back to, and no other control that would do it.
 *
 *              A material with no price in the column sorts LAST in both
 *              directions. A blank is not cheap and not dear, and letting it
 *              float to the top of "cheapest first" would put the rows nobody
 *              can use above the ones being looked for.
 */

/** asc → desc → off → asc. */
export function nextSortDirection(current) {
  if (current === 'asc') return 'desc';
  if (current === 'desc') return null;
  return 'asc';
}

const isBlank = (value) =>
  value === null || value === undefined || value === '' || !Number.isFinite(Number(value));

/**
 * Compare two costs for the given direction, blanks last either way.
 * @param {number|string|null} a
 * @param {number|string|null} b
 * @param {'asc'|'desc'} direction
 */
export function compareCost(a, b, direction = 'asc') {
  const blankA = isBlank(a);
  const blankB = isBlank(b);
  if (blankA || blankB) return blankA === blankB ? 0 : (blankA ? 1 : -1);
  return direction === 'desc' ? Number(b) - Number(a) : Number(a) - Number(b);
}

/**
 * Rows in cost order. Stable: materials at the same price — and every blank —
 * keep the order they came in, which is the workbook's. With no direction the
 * rows come back untouched.
 *
 * @param {Array}    rows
 * @param {Function} costOf  row => cost per tonne, or null when unpriced
 * @param {'asc'|'desc'|null} direction
 */
export function sortRowsByCost(rows, costOf, direction) {
  if (direction !== 'asc' && direction !== 'desc') return rows;
  return rows
    .map((row, index) => ({ row, index, cost: costOf(row) }))
    .sort((x, y) => compareCost(x.cost, y.cost, direction) || x.index - y.index)
    .map(({ row }) => row);
}
